import { update } from "../core/signals/primitives.js";
import { createMachineReactor } from "../core/reactors/create-machine-reactor.js";
import { isResolvedPresentation, isResolvedTrack } from "../media/types/index.js";
import { findTrackById } from "../media/utils/tracks.js";
//#region src/playback/behaviors/establish-start-media-time.ts
/**
* A single type's own media-timeline origin: `baseMediaDecodeTime/timescale − segmentStartTime` (the `segmentStartTime`
* term makes it the stream origin even when the first loaded segment isn't the 0th). `undefined` until timescale +
* baseMediaDecodeTime + segmentStartTime are all present.
*/
function ownOrigin(data) {
	const { timescale, baseMediaDecodeTime, segmentStartTime } = data ?? {};
	return timescale != null && baseMediaDecodeTime != null && segmentStartTime != null ? baseMediaDecodeTime / timescale - segmentStartTime : void 0;
}
/**
* Origins below this magnitude (seconds) are treated as `0` — the derive returns `0`, so the presentation is left on
* its native (~0-based) timeline and no `timestampOffset` is set (the loader stamp no-ops on a derived `0`). Ordinary
* VOD carries a small nonzero encode origin (audio priming, first-frame CTS, edit lists); relocating by a sub-second
* amount is pointless, and setting `timestampOffset` at all can ripple edge segments. Relocation targets streams with
* an intentional large origin (instant clips, bipbop @10s).
*
* Absolute basis, not proportional: the DTS-below-zero / ripple risk scales with the origin's absolute size, not with
* the presentation's duration. Also snaps negatives to `0` — a negative origin would relocate _forward_, which is never
* the intent.
*/
const NEAR_ZERO_ORIGIN_THRESHOLD = 1;
/** Snap a below-threshold (incl. negative) origin to `0` so it isn't relocated. */
function thresholdOrigin(origin) {
	return origin < NEAR_ZERO_ORIGIN_THRESHOLD ? 0 : origin;
}
/**
* The **default** — relocate the whole presentation by one shared origin: the `min` across the _selected_ A/V tracks'
* own origins, denormalized onto every type. This single reduce subsumes the "per-type" and "shared" tiers:
*
* - **aligned A/V** — `min` equals each origin (they're equal), so it matches per-type;
* - **skewed A/V** (e.g. Apple's 44ms audio-lead) — `min` keeps every track's earliest DTS ≥ 0 (relocating by ≤ each own
*   origin never drives one negative) _and_ preserves the real skew (per-type would flatten it, desyncing A/V);
* - **single type / muxed** — `min` of the one origin is that origin.
*
* Returns `undefined` for every type until all _selected_ types have a complete origin (the shared-`min` barrier).
* Which types must contribute is read from `ctx` (the selected v/a ids); with no selection context it coordinates
* across whatever types have data. A shared origin below {@link NEAR_ZERO_ORIGIN_THRESHOLD} is returned as `0` (native
* — ordinary ~0-PTS VOD isn't relocated).
*/
const deriveSharedMinStartMediaTime = (containerData, ctx) => {
	const contributingTypes = [];
	if (ctx.selectedVideoTrackId != null) contributingTypes.push("video");
	if (ctx.selectedAudioTrackId != null) contributingTypes.push("audio");
	const origins = (contributingTypes.length > 0 ? contributingTypes : Object.keys(containerData)).map((type) => ownOrigin(containerData[type]));
	if (origins.length === 0 || origins.some((origin) => origin === void 0)) return {};
	const shared = thresholdOrigin(Math.min(...origins));
	const out = {};
	for (const type of Object.keys(containerData)) out[type] = shared;
	return out;
};
/**
* Coordination-axis _off_ — each type relocates by its own origin, independently. Not the default: it flattens real A/V
* skew (see {@link deriveSharedMinStartMediaTime}). Kept as an opt-in for compositions that know their A/V is aligned
* and want to skip the shared-`min` barrier (each type stamps as soon as its own origin is discovered).
*
* Caution: {@link NEAR_ZERO_ORIGIN_THRESHOLD} applies independently per type here — if two types' origins straddle it,
* one snaps to `0` and the other doesn't, injecting up to a threshold's worth of A/V offset. Safe for the intended
* aligned-A/V opt-in; see `internal/design/spf/live-presentation-timeline-model.md` (collision 5).
*/
const derivePerTypeStartMediaTime = (containerData) => {
	const out = {};
	for (const [type, data] of Object.entries(containerData)) {
		const origin = ownOrigin(data);
		out[type] = origin === void 0 ? void 0 : thresholdOrigin(origin);
	}
	return out;
};
/**
* The anchor-source track: the selected video track, falling back to audio (audio-only). Deterministic — never "first
* resolved", which is race-dependent under concurrent resolves.
*/
function referenceTrackId(ctx) {
	return ctx.selectedVideoTrackId ?? ctx.selectedAudioTrackId;
}
/**
* The live-anchor first-parse gate (the "parses align" step of the establishment order). Open for the reference track
* itself — its local-from-0 placement _is_ the presentation timeline (presentation-0 = the join point). Every other
* track holds until the reference's first parse settles the anchor question: no PDT → local placement is already
* correct, proceed; PDT anchor → hold until the establishment reactor stamps it onto this track, so the parse takes the
* `placeOnAnchor` path. Establishment is sticky-once, so an unanchored first parse would be permanently misaligned —
* that's the race this gate closes.
*
* Pairs with the {@link establishStartMediaTime} reactor (the stamp that opens the anchored branch); wire both or
* neither.
*/
const gateFirstParseOnAnchor = (presentation, ctx, trackId) => {
	const referenceId = referenceTrackId(ctx);
	if (referenceId === void 0 || referenceId === trackId) return true;
	if (!isResolvedPresentation(presentation)) return false;
	const reference = findTrackById(presentation, referenceId);
	if (!reference) return true;
	if (!isResolvedTrack(reference)) return false;
	if (reference.startDate === void 0) return true;
	return findTrackById(presentation, trackId)?.startDate !== void 0;
};
/**
* Stamp the frozen wall-clock anchor as `startDate` onto every track that lacks one (idempotent — same reference when
* nothing moved). Unresolved shells pick it up as the `placeOnAnchor` preset; covering _all_ tracks in one pass means
* any track selected later — an ABR rung, another audio language, late captions — resolves already anchored. The
* reference track's own parser-computed `startDate` reads back as the anchor, so it's naturally left untouched.
*/
function stampStartDates(presentation, anchor) {
	let changed = false;
	const selectionSets = presentation.selectionSets.map((selectionSet) => ({
		...selectionSet,
		switchingSets: selectionSet.switchingSets.map((switchingSet) => ({
			...switchingSet,
			tracks: switchingSet.tracks.map((track) => {
				if (track.startDate !== void 0) return track;
				changed = true;
				return {
					...track,
					startDate: anchor
				};
			})
		}))
	}));
	return changed ? {
		...presentation,
		selectionSets
	} : presentation;
}
/** Stamp the derived per-track `startMediaTime` onto the model (idempotent — same reference when nothing moved). */
function stampTracks(presentation, startMediaTimes) {
	let changed = false;
	const selectionSets = presentation.selectionSets.map((selectionSet) => ({
		...selectionSet,
		switchingSets: selectionSet.switchingSets.map((switchingSet) => ({
			...switchingSet,
			tracks: switchingSet.tracks.map((track) => {
				const startMediaTime = startMediaTimes[track.type];
				if (startMediaTime === void 0 || track.startMediaTime === startMediaTime) return track;
				changed = true;
				return {
					...track,
					startMediaTime
				};
			})
		}))
	}));
	return changed ? {
		...presentation,
		selectionSets
	} : presentation;
}
function establishStartMediaTimeSetup({ state, config = {} }) {
	const derive = config.deriveStartMediaTime ?? deriveSharedMinStartMediaTime;
	const selectionContext = () => ({
		selectedVideoTrackId: state.selectedVideoTrackId?.get(),
		selectedAudioTrackId: state.selectedAudioTrackId?.get()
	});
	/** Established once the selected A/V tracks (whichever exist) carry `startMediaTime`. */
	const established = () => {
		const presentation = state.presentation.get();
		if (!isResolvedPresentation(presentation)) return false;
		const ids = [state.selectedVideoTrackId?.get(), state.selectedAudioTrackId?.get()].filter((id) => id !== void 0);
		return ids.length > 0 && ids.every((id) => findTrackById(presentation, id)?.startMediaTime !== void 0);
	};
	return createMachineReactor({
		initial: "inactive",
		monitor: () => {
			if (!isResolvedPresentation(state.presentation.get())) return "inactive";
			return established() ? "established" : "monitoring";
		},
		states: {
			inactive: { entry: () => state.mediaContainerData.set(void 0) },
			monitoring: { effects: [() => {
				const presentation = state.presentation.get();
				if (!isResolvedPresentation(presentation)) return;
				const referenceId = referenceTrackId(selectionContext());
				const reference = referenceId === void 0 ? void 0 : findTrackById(presentation, referenceId);
				if (!reference || !isResolvedTrack(reference)) return;
				const anchor = reference.startDate;
				if (anchor === void 0) return;
				update(state.presentation, (current) => stampStartDates(current, anchor));
			}, () => {
				const containerData = state.mediaContainerData.get();
				if (!containerData) return;
				const startMediaTimes = derive(containerData, selectionContext());
				update(state.presentation, (current) => stampTracks(current, startMediaTimes));
			}] },
			established: {}
		}
	});
}
const establishStartMediaTime = {
	stateKeys: ["presentation", "mediaContainerData"],
	contextKeys: [],
	setup: establishStartMediaTimeSetup
};
//#endregion
export { derivePerTypeStartMediaTime, deriveSharedMinStartMediaTime, establishStartMediaTime, gateFirstParseOnAnchor };
